/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from "lodash";
import debounce from "debounce";
import { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useDispatch, useSelector } from "react-redux";
import { Copy, Edit, Trash } from "@jda/lui-common-icon-library-mui5";
import { LuiThreeDotMenu } from "@jda/lui-common-component-library-mui5";
import { AppBar, Box, Grid, Tab, Tabs, Tooltip, Typography } from "@mui/material";

import { useStyles } from "./styles";
import Realms from "../realms/Realms";
import { MESSAGES, TITLES } from "./constants";
import Entitlements from "../entitlements/Entitlements";
import { TabPanel } from "../../shared/TabPanel/TabPanel";
import SearchBar from "../../shared/searchLists/SearchBar";
import SearchList from "../../shared/searchLists/SearchList";
import SnackbarAlert from "../../shared/snackbar/SnackbarAlert";
import { selectLoader } from "../../store/loader/loaderSelector";
import { actions, resources } from "../checkPermission/constants";
import SearchListItem from "../../shared/searchLists/SearchListItem";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { SNACKBAR_ALERT_DEFAULT_PROPS } from "../../shared/constants";
import EmptyComponent from "../../shared/emptyComponent/EmptyComponent";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";
import { ThreeDotOption } from "../../shared/ThreeDotOption/ThreeDotOption";
import { selectAllAssignedPermissions } from "../../store/auth/authSelector";
import { checkAuthzPermission } from "../checkPermission/CheckViewPermission";
import { selectSelectedCustomer } from "../../store/customer/customerSelector";
import { setSnackBarDisplay } from "../../store/snackBar/snackBarActionCreator";
import useWindowDimensions from "../../shared/windowDimensions/windowDimensions";
import AddSubEnterpriseDialog from "../addSubEnterpriseDialog/AddSubEnterpriseDialog";
import DeleteSubEnterpriseDialog from "../deleteSubEnterpriseDialog/DeleteSubEnterpriseDialog";
import { setSelectedSubEnterprisesAction } from "../../store/subEnterprise/subEnterpriseActionCreator";
import { getSubEnterprise, removeSubEnterprise, resetDataOnChangeCustomer } from "../../store/subEnterprise/subEnterprise";
import { selectNoSubEnterprise, selectSelectedSubEnterprise, selectAllSubEnterprises } from "../../store/subEnterprise/subEnterpriseSelector";

const SubEnterprise = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const showLoader = useSelector(selectLoader);
  const noSubEnterprise = useSelector(selectNoSubEnterprise);
  const selectedCustomer = useSelector(selectSelectedCustomer);
  const subEnterpriseData = useSelector(selectAllSubEnterprises)
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const selectedSubEnterprise = useSelector(selectSelectedSubEnterprise);

  const [value, setValue] = useState(0);
  const [action, SetAction] = useState("");
  const [searchString, setSearchString] = useState("");

  const addAccountPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.CREATE, assignedPermissions.resources[resources.ACCOUNT]);
  const editAccountPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.EDIT, assignedPermissions.resources[resources.ACCOUNT]);
  const deleteAccountPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.DELETE, assignedPermissions.resources[resources.ACCOUNT]);

  useEffect(() => {
    if (selectedCustomer?.id) {
      getAllSubEnterPriseList();
    }
  }, [selectedCustomer?.id]);

  const getAllSubEnterPriseList = async () => {
    const subEnterprisePayload = {
      customerId: selectedCustomer.id,
      pageSize: 500,
      pageNo: 1,
      searchString: "",
    }
    resetDataOnChangeCustomer(dispatch);
    getSubEnterprise(dispatch, subEnterprisePayload);
  };

  const handleMenuClick = (actionName: string) => {
    dispatch(setSelectedSubEnterprisesAction(selectedSubEnterprise));
    SetAction(actionName);
  };

  const handleDialogClose = () => {
    SetAction("");
  };

  const items: any = [
    editAccountPermission ? {
      render: <ThreeDotOption icon={<Edit fontSize="small" />} text={TITLES.EDIT} />,
      onClick: () => handleMenuClick("Edit"),
      autoClose: true,
    } : undefined,
    deleteAccountPermission ? {
      render: <ThreeDotOption icon={<Trash fontSize="small" />} text={TITLES.DELETE} />,
      onClick: () => handleMenuClick("Delete"),
      autoClose: true,
    } : undefined,
  ];

  const handleChange = (_event: any, newValue: any) => {
    setValue(newValue);
  };

  const snackBarMessage = {
    show: false,
    autoHideDuration: 5000,
    message: "",
    severity: "success" as "success" | "error",
  };

  const modules = [
    {
      component: <Realms />,
      name: TITLES.REALMS,
    }
    // },
    // editAccountPermission ? {
    //   component: <Entitlements />,
    //   name: TITLES.ENTITLEMENTS,
    // } : undefined,
  ];

  const errors = {
    message: "",
  };

  const handleClearSearchInput = () => {
    const subEnterprisePayload = {
      customerId: selectedCustomer.id,
      pageSize: 500,
      pageNo: 1,
      searchString: '',
    };
    getSubEnterprise(dispatch, subEnterprisePayload);
  };

  const handleCopy = () => {
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'success',
        message: MESSAGES.COPIED_TO_CLIPBOARD,
      })
    );
  }

  const onSearchTextBoxChange = async (searchValue: string) => {
    setSearchString(searchValue);
    if (searchValue.trim() !== "" && searchValue.length > 2) {
      const subEnterprisePayload = {
        customerId: selectedCustomer.id,
        pageSize: 500,
        pageNo: 1,
        searchString: searchValue.trim(),
      };
      resetDataOnChangeCustomer(dispatch);
      await getSubEnterprise(dispatch, subEnterprisePayload);
    }
    dispatch(hideLoaderAction(MESSAGES.SUBENTERPRISE_TITLE));
  };

  const handleListItemClick = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>, listItem: any) => {
    dispatch(setSelectedSubEnterprisesAction(listItem));
    // dispatch(resetRealmAction());
  };

  const handleDeleteSubEnterprise = async () => {
    const payload = {
      id: selectedSubEnterprise.id,
      customerId: selectedCustomer.id
    }
    await removeSubEnterprise(dispatch, payload);
    SetAction("");
    dispatch(hideLoaderAction(MESSAGES.SUBENTERPRISE_TITLE));
  };

  const debounceSearch = debounce(onSearchTextBoxChange, 500);

  return (
    <Box className={classes.wrapRoot} height={height}>
      <Grid>
        <Box>
          <Typography variant="h6" className={classes.componentHeading}>
            {MESSAGES.SUBENTERPRISE_TITLE}
          </Typography>
        </Box>
      </Grid>
      {(subEnterpriseData && subEnterpriseData.length === 0 && !searchString.length) &&
        <Grid container className={classes.emptyScreenRoot}>
          <Grid item xs={12} sm={12}>
            <EmptyComponent
              subTitle=""
              buttonText={MESSAGES.SUBENTERPRISE_ADD_BUTTON}
              title={MESSAGES.SUBENTERPRISE_EMPTY_SCREEN_TITLE}
              handleOnCreateAction={() => handleMenuClick("Add")}
              disableCreateAction={!addAccountPermission}
            />
          </Grid>
        </Grid>
      }
      <Grid container className={(subEnterpriseData && subEnterpriseData.length > 0) || (searchString.length) ? classes.root : classes.hide}>
        <Grid item xs={12} sm={3} className={classes.topLeftHeaderContainer}>
          <SearchBar
            addButtonDialog={handleMenuClick}
            showAddButton={addAccountPermission}
            onSearchTextBoxChange={debounceSearch}
            placeHolder={TITLES.SUBENTERPRISE_NAME}
            onClearTextInput={handleClearSearchInput}
            addButtonMessage={MESSAGES.SUBENTERPRISE_ADD_BUTTON}
          />
        </Grid>
        <Grid item xs={12} sm={9} className={classes.detailsBox}>
          {selectedSubEnterprise.id &&
            <>
              <Typography variant="body1" gutterBottom>
                <Box className={classes.detailsBoxItems}>
                  <Box fontWeight="fontWeightBold">
                    {TITLES.ACCOUNT_KEY}
                  </Box>
                  <Box fontWeight="fontWeightMedium" className={classes.detailValueText}>
                    {selectedSubEnterprise?.accountKey}
                  </Box>
                  <Tooltip title="copy">
                    <Box data-testid="toolTip-sign">
                      <CopyToClipboard text={selectedSubEnterprise?.accountKey} onCopy={handleCopy}>
                        <Copy className={`${classes.pointer}`} fontSize="small" />
                      </CopyToClipboard>
                    </Box>
                  </Tooltip>
                </Box>
              </Typography>
            </>
          }
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={3}>
          <Box component="div" className={classes.listItemPaper}>
            <Box
              className={[
                classes.listItemPaper,
                !noSubEnterprise ? classes.listContainerBorder : "",
              ].join(" ")}
            >
              {(subEnterpriseData && subEnterpriseData.length > 0) || (searchString.length) ?
                <SearchList
                  dense={false}
                  minHeight={70}
                  addDivider={true}
                  height={height - 230}
                  disableGutters={false}
                  key={"SubEntepriseList"}
                  data={subEnterpriseData}
                  onClick={handleListItemClick}
                  selectedItem={selectedSubEnterprise}
                  listStyle={classes.searchListContainer}
                  renderItem={(item) => (
                    <SearchListItem
                      key={item?.id}
                      showAvatarContent={true}
                      ListPrimaryContent={
                        <Box>
                          <Typography variant="body1" className={classes.textEllipsisPrimary}>
                            <Box
                              data-testid="name"
                              component="span"
                              fontWeight="fontWeightBold"
                              title={item?.name}
                            >
                              {item?.name}
                            </Box>
                          </Typography>
                        </Box>
                      }
                      ListSecondaryContent={
                        <Box>
                          <Typography variant="body1" color="textSecondary" className={classes.textEllipsisSecondary}>
                            <Box
                              component="span"
                              fontWeight="fontWeightNormal"
                              data-testid="subEnterprise-id"
                              title={item?.displayName}
                            >
                              {item?.displayName}
                            </Box>
                          </Typography>
                        </Box>
                      }
                      ListSecondaryActionContent={
                        editAccountPermission || deleteAccountPermission ?
                          <LuiThreeDotMenu
                            items={_.compact(items)}
                            ariaLabel="Three Dot Menu"
                          /> : null
                      }
                    />
                  )}
                /> :
                null
              }
              {searchString.length > 0 && subEnterpriseData.length === 0 && (
                <EmptyComponent
                  subTitle={''}
                  disableCreateAction={true}
                  title={MESSAGES.NO_RESULTS_FOUND}
                />
              )}
              {errors && <ErrorMessage {...errors} />}
            </Box>
          </Box>
        </Grid>
        {subEnterpriseData.length ?
          <Grid item xs={12} sm={9}>
            <Box className={classes.tabs}>
              <AppBar
                elevation={0}
                position="relative"
                data-testid="realm-appbar"
                className={classes.appBar}
              >
                <Tabs
                  value={value}
                  variant="fullWidth"
                  onChange={handleChange}
                  className={classes.tabs}
                >
                  {_.compact(modules).map((module, key) => (
                    <Tab
                      key={key}
                      label={module.name}
                      className={classes.tab}
                    />
                  ))}
                </Tabs>
              </AppBar>
            </Box>
            <Grid container>
              <Grid item xs={12} sm={12}>
                <Box className={classes.rightContainer}>
                  {_.compact(modules).map((module, key) => (
                    <TabPanel value={value} index={key} key={key}>
                      {module.component}
                    </TabPanel>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Grid> : null
        }
      </Grid>
      <DeleteSubEnterpriseDialog
        onClose={handleDialogClose}
        isOpen={action === "Delete"}
        subEnterpriseData={selectedSubEnterprise}
        deleteSubEnterprise={handleDeleteSubEnterprise}
      />
      <AddSubEnterpriseDialog
        isEdit={action === 'Edit'}
        onClose={handleDialogClose}
        subEnterpriseData={selectedSubEnterprise}
        isOpen={action === "Add" || action === 'Edit'}
      />
      {snackBarMessage.show &&
        <SnackbarAlert
          show={snackBarMessage.show}
          autoHideDuration={snackBarMessage.autoHideDuration}
          message={snackBarMessage.message}
          severity={snackBarMessage.severity}
        />
      }
    </Box>
  );
};

export default SubEnterprise;